import { defineComponent, PropType } from "vue";
import { renderMarkdownAST } from "../../../utils/markdownRenderer.js";
import { renderMessageComponents } from "./MessageComponents.js";
import MessageImageAttachment from "./MessageImageAttachment.js";
import type { ParsedMessageLog } from "./messageParser.js";

type MessageAttachment = {
    contentType?: unknown;
    name?: unknown;
    proxyURL?: unknown;
    url?: unknown;
};

function readAttachments(log: ParsedMessageLog): MessageAttachment[] {
    const attachments = log.extra.attachments;

    return Array.isArray(attachments)
        ? attachments as MessageAttachment[]
        : [];
}

function readString(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined;
}

function renderAttachment(attachment: MessageAttachment, onImageSettled?: () => void) {
    const contentType = readString(attachment.contentType);
    const name = readString(attachment.name) ?? "fichier";
    const proxyURL = readString(attachment.proxyURL);
    const url = readString(attachment.url);

    if (contentType?.startsWith("image/")) {
        return (
            <MessageImageAttachment
                src={proxyURL || url}
                href={url || proxyURL}
                name={name}
                onSettled={onImageSettled}
            />
        );
    }

    if (contentType?.startsWith("video/")) {
        return (
            <video
                class="log-message-attachment"
                src={proxyURL || url}
                title={name}
                preload="metadata"
                controls
            />
        );
    }

    if (contentType?.startsWith("audio/")) {
        return (
            <audio
                class="log-message-attachment"
                src={proxyURL || url}
                title={name}
                preload="metadata"
                controls
            />
        );
    }

    return (
        <a href={url || proxyURL} download={name}>
            Fichier {name}
        </a>
    );
}

export default defineComponent({
    props: {
        log: {
            type: Object as PropType<ParsedMessageLog>,
            required: true,
        },
        onImageSettled: {
            type: Function as PropType<() => void>,
            default: undefined,
        },
    },

    setup(props) {
        return () => {
            const content = readString(props.log.extra.content);

            return (
                <>
                {!!content?.length && (
                    <div class="log-message-content">
                        {props.log.parsedContent
                            ? renderMarkdownAST(props.log.parsedContent)
                            : content}
                    </div>
                )}

                {!!props.log.extra.embeds?.length && (
                    <div class="log-message-embeds">
                        {props.log.extra.embeds.map((embed) => (
                            <div class="log-message-embed">
                                {embed.parsedTitle && (
                                    <div class="log-message-embed-title">
                                        {renderMarkdownAST(embed.parsedTitle)}
                                    </div>
                                )}

                                {embed.parsedDescription && (
                                    <div class="log-message-embed-description">
                                        {renderMarkdownAST(embed.parsedDescription)}
                                    </div>
                                )}

                                {!!embed.fields?.length && (
                                    <div class="log-message-embed-fields">
                                        {embed.fields.map((field) => (
                                            <div class={{
                                                "log-message-embed-field"        : true,
                                                "log-message-embed-field-inline" : field.inline,
                                            }}>
                                                <div class="log-message-embed-field-name">
                                                    {renderMarkdownAST(field.parsedName)}
                                                </div>

                                                <div class="log-message-embed-field-value">
                                                    {renderMarkdownAST(field.parsedValue)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {embed.parsedFooter && (
                                    <div class="log-message-embed-footer">
                                        {renderMarkdownAST(embed.parsedFooter)}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!!readAttachments(props.log).length && (
                    <div class="log-message-attachments">
                        {readAttachments(props.log).map((attachment) => renderAttachment(attachment, props.onImageSettled))}
                    </div>
                )}

                {renderMessageComponents(props.log.extra.components, props.onImageSettled)}
                </>
            );
        };
    },
});
